// src/features/editor/components/blocks/previews/MatchBlockPreview.tsx
import type { MatchBlock, MatchPair } from '@/types/blocks'
import { useStrictLang } from './design/strictLangContext'
import type { ContentLang } from '@/features/editor/editorSlice'
import { getLocalizedDisplay } from '@/features/editor/lib/localizedField'
import { QuizShell, blockTheme } from '@teach/ui'
import { matchOrder } from '../../preview/answerState'
import { verdictGrade } from '../../preview/verdictView'
import type { PreviewRun } from '../../preview/previewRun'
import { AnswerFeedback } from './AnswerFeedback'
import { MatchCard } from './MatchCard'
import { matchCardView, type MatchSide } from './matchPick'
import { PreviewShell } from './PreviewShell'
import { useQuizMascot } from './quizMascotContext'
import { useMatchPick } from './useMatchPick'
import { pickMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: MatchBlock
  contentLang: ContentLang
  /** Прохождение: пары собираются тапами и красятся цветом пары, как в плеере. */
  run?: PreviewRun
}

// Заголовок — не наш копирайт, а ровно тот, что читатель видит в плеере
// (academy-frontend MatchBlock.tsx: t('Сопоставьте термин с описанием')).
// У match-блока своего поля вопроса в контракте нет вовсе, поэтому подпись
// захардкожена в обоих репозиториях — и обязана совпадать дословно, иначе
// студия показывает автору экран, которого не будет.
// Текст — `messages.matchTitle`.

/**
 * На ХОЛСТЕ автор правит пары посреди ответа: раскладка, снятая при другом
 * числе пар, там недействительна (новая пара осталась бы без правой части).
 * В прохождении она остаётся — исчезнувшая пара даёт строку-заглушку.
 */
const rightOrder = (run: PreviewRun | undefined, pairs: number): number[] => {
  const identity = Array.from({ length: pairs }, (_, i) => i)
  if (!run) return identity
  const answered = matchOrder(run.answer)
  return run.onCanvas === true && answered.length !== pairs ? identity : answered
}

export const MatchBlockPreview = ({ block, contentLang, run }: Props) => {
  // Экран ученика: кнопки и заголовки — на языке курса, как в плеере.
  const p = pickMessages(messages, contentLang)
  // Холст и миниатюры редактора — строго язык правки; прохождение — как плеер.
  const strictLang = useStrictLang() && (!run || run.onCanvas === true)
  const mascot = useQuizMascot(run?.verdict)
  // Показ, не правка: у английского курса пары лежат в *En, а у английского
  // курса на слоте RU базовые поля `null` — getLocalizedDisplay(..., false)
  // делает симметричный фолбэк в обе стороны.
  const pickText = (p: MatchPair, side: 'leftText' | 'rightText') =>
    getLocalizedDisplay(p, side, contentLang, strictLang)

  // Порядок правой колонки: в редакторе авторский, в прохождении — перемешанный
  // при входе на экран; карточки не двигаются, пару отмечает цвет.
  const order = rightOrder(run, block.pairs.length)
  const { selection, pick } = useMatchPick(run)
  // «Оценён ли» разбирается общим `verdictGrade`, а не сравнением с `null`.
  const graded = run ? verdictGrade(run.verdict) !== 'ungraded' : false
  const view = (side: MatchSide, id: number) =>
    run ? matchCardView(selection, graded, side, id) : { state: 'idle' as const }
  const locked = Boolean(run?.verdict)

  return (
    <PreviewShell
      cta={run?.ctaLabel ?? p.confirm}
      ctaState={run?.ctaState}
      onCta={run?.onCta}
      ctaDisabled={run?.ctaDisabled}
      style={{ backgroundColor: blockTheme.quiz.bg }}
      contentClassName="min-h-0"
      verdictPlate={run ? <AnswerFeedback lang={contentLang} verdict={run.verdict} /> : undefined}
    >
      <QuizShell question={p.matchTitle} mascot={mascot} fitMascot>
        {/* Одна сетка на обе колонки, как `.grid` плеера (MatchBlock.module.css):
            ряды равны самой высокой карточке, пара стоит на одной линии.
            Колонки — `display: contents`, их карточки ложатся в общую сетку. */}
        <div
          className="grid w-full grid-flow-col grid-cols-[repeat(2,minmax(0,1fr))] gap-x-3 gap-y-2"
          style={{ gridTemplateRows: `repeat(${block.pairs.length}, 1fr)` }}
        >
          <div className="contents" data-testid="match-left">
            {block.pairs.map((p, row) => (
              <MatchCard
                key={`l-${row}`}
                text={pickText(p, 'leftText')}
                {...view('left', row)}
                disabled={locked}
                onSelect={run ? () => pick('left', row) : undefined}
              />
            ))}
          </div>
          <div className="contents" data-testid="match-right">
            {/* Строк СТОЛЬКО ЖЕ, сколько слева: `order` посчитан при входе на
                экран, а блок мог ужаться под ним (запуск/фоновый коммит).
                Пропускать строку нельзя — уцелевшие карточки уехали бы вверх, и
                автор увидел бы раскладку, которой у ученика не будет; строка без
                пары рисуется НЕЙТРАЛЬНОЙ заглушкой (неразрывный пробел держит
                высоту) и в парах не участвует. */}
            {block.pairs.map((_, row) => {
              const id = order[row]
              const pair = block.pairs[id]
              return pair ? (
                <MatchCard
                  key={`r-${row}`}
                  text={pickText(pair, 'rightText')}
                  {...view('right', id)}
                  disabled={locked}
                  onSelect={run ? () => pick('right', id) : undefined}
                />
              ) : (
                // JSX-строка не разбирает escape-последовательности — nbsp
                // приходит выражением, иначе в карточку уехало бы «\u00A0».
                <MatchCard key={`r-${row}`} text={'\u00A0'} state="idle" disabled />
              )
            })}
          </div>
        </div>
      </QuizShell>
    </PreviewShell>
  )
}
